import StatCard from "@/components/common/StatCard";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import {
  Users, UserCheck, Contact, ShieldCheck, GitBranch, Trophy, BadgeDollarSign, Wallet, MapPin,
  Phone, MessageCircle, CalendarClock, AlertTriangle, Target,
} from "lucide-react";

// mode: "all" passes date+salesperson filters, "sp" passes salesperson only, "none" passes nothing
const k = (id, label, key, icon, tone, to, mode = "all", fmt = "number", hint) => ({ id, label, key, icon, tone, to, mode, fmt, hint });

export const KPI_CONFIG = {
  super_admin: [
    k("kpi-active-users", "Active Users", "active_users", Users, "navy", "/users", "none"),
    k("kpi-total-leads", "Total Leads", "total_leads", Contact, "teal", "/leads"),
    k("kpi-dup-prevented", "Duplicates Prevented", "duplicates_prevented", ShieldCheck, "amber", "/reports?tab=reports&report=duplicate_review"),
    k("kpi-active-pipeline", "Active Pipeline", "active_pipeline", GitBranch, "sky", "/pipeline"),
    k("kpi-won", "Won Deals", "won_deals", Trophy, "emerald", "/reports?tab=reports&report=won_deals"),
    k("kpi-verified", "Payment Verified", "payment_verified", BadgeDollarSign, "violet", "/reports?tab=reports&report=payments"),
    k("kpi-revenue", "Revenue (manual)", "revenue_manual", Wallet, "teal", "/reports?tab=reports&report=payments", "all", "currency", "Manually verified only"),
    k("kpi-conversion", "Lead Conversion", "conversion_rate", Target, "navy", "/leads?status=Converted", "all", "pct"),
  ],
  manager: [
    k("kpi-team", "Team Members", "team_members", Users, "navy", "/work-map", "sp"),
    k("kpi-working", "Working Now", "employees_working", MapPin, "emerald", "/work-map", "sp"),
    k("kpi-new-leads", "New Leads", "new_leads", Contact, "teal", "/leads"),
    k("kpi-no-action", "Leads w/o Next Action", "leads_without_action", AlertTriangle, "amber", "/leads?no_action=1"),
    k("kpi-calls", "Team Calls", "team_calls", Phone, "sky", "/activities?view=list&type=Phone+Call"),
    k("kpi-visits", "Team Visits", "team_visits", MapPin, "violet", "/reports?tab=reports&report=visits"),
    k("kpi-won", "Won Deals", "won_deals", Trophy, "emerald", "/reports?tab=reports&report=won_deals"),
    k("kpi-verified", "Payment Verified", "payment_verified", BadgeDollarSign, "teal", "/reports?tab=reports&report=payments"),
  ],
  field_executive: [
    k("kpi-work-status", "Work Status", "work_status", MapPin, (v) => (v.work_status === "Working" ? "emerald" : "navy"), "/work-map", "none", "text"),
    k("kpi-new-leads", "My Leads", "new_leads", Contact, "teal", "/leads"),
    k("kpi-followups", "Follow-ups Due", "followups_due", CalendarClock, "amber", "/activities?view=followups", "none"),
    k("kpi-overdue", "Overdue Follow-ups", "overdue_followups", AlertTriangle, "rose", "/activities?view=followups", "none"),
    k("kpi-calls", "Calls Logged", "calls_logged", Phone, "sky", "/activities?view=list&type=Phone+Call"),
    k("kpi-whatsapp", "WhatsApp", "whatsapp", MessageCircle, "emerald", "/activities?view=list&type=WhatsApp"),
    k("kpi-visits", "Visits Completed", "visits_completed", MapPin, "violet", "/work-map", "none"),
    k("kpi-won", "Won Deals", "won_deals", Trophy, "teal", "/pipeline"),
  ],
  sales_partner: [
    k("kpi-submitted", "Leads Submitted", "leads_submitted", Contact, "teal", "/leads"),
    k("kpi-approved", "Leads Approved", "leads_approved", UserCheck, "emerald", "/leads"),
    k("kpi-demos", "Demos Requested", "demos_requested", CalendarClock, "navy", "/activities?view=list&type=Demo"),
    k("kpi-won", "Won Deals", "won_deals", Trophy, "violet", "/pipeline"),
    k("kpi-verified", "Payment Verified", "payment_verified", BadgeDollarSign, "sky", "/pipeline"),
  ],
};

const FMT = { number: fmtNumber, currency: fmtCurrency, pct: (v) => `${v ?? 0}%`, text: (v) => v ?? "—" };

function buildLink(item, query, salesperson) {
  const extra = item.mode === "all" ? query : item.mode === "sp" && salesperson ? `salesperson=${encodeURIComponent(salesperson)}` : "";
  if (!extra) return item.to;
  return `${item.to}${item.to.includes("?") ? "&" : "?"}${extra}`;
}

export default function KpiGrid({ role, kpis = {}, query, salesperson }) {
  const items = KPI_CONFIG[role] || [];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
      {items.map((item, i) => (
        <div key={item.id} className="animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both" style={{ animationDelay: `${i * 40}ms` }}>
          <StatCard testid={item.id} label={item.label} value={FMT[item.fmt](kpis[item.key])} icon={item.icon}
            tone={typeof item.tone === "function" ? item.tone(kpis) : item.tone} hint={item.hint}
            to={buildLink(item, query, salesperson)} />
        </div>
      ))}
    </div>
  );
}
